import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  FolderOpen, 
  Users, 
  Clock, 
  Bell,
  Search,
  Menu,
  X,
  User,
  LogOut,
  ChevronDown,
  Calendar,
  FileText
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import DatePicker from './DatePicker';
import TeamSelector from './TeamSelector';
import { AVAILABLE_CATEGORIES } from '../utils/categoryUtils';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, actions } = useApp();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);

  // Réinitialiser le filtre par catégorie quand on quitte la page Projets
  React.useEffect(() => {
    if (location.pathname !== '/projets' && state.filters.categorie) {
      actions.setFilters({ categorie: '' });
    }
  }, [location.pathname, state.filters.categorie, actions]);

  const navigation = [
    { name: 'Tableau de bord', href: '/', icon: Home },
    { name: 'Projets', href: '/projets', icon: FolderOpen },
    { name: 'Personnes & Équipes', href: '/personnes', icon: Users },
    { name: 'Capacity Planning', href: '/capacity-planning', icon: Calendar },
    { name: 'Saisie temps', href: '/saisie-temps', icon: Clock },
    { name: 'Facturation', href: '/facturation', icon: FileText },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // Fonction pour déterminer le type de select et les données à afficher
  const getSelectData = () => {
    const currentPath = location.pathname;
    
    // Page PersonneDetail (pattern: /personnes/:id)
    if (currentPath.match(/^\/personnes\/\d+$/)) {
      const currentPersonneId = currentPath.split('/')[2];
      return {
        type: 'personne',
        currentId: currentPersonneId,
        items: state.personnes?.data || [],
        placeholder: 'Choisir une personne...',
        getLabel: (item) => `${item.prenom} ${item.nom}`,
        getValue: (item) => item.id.toString(),
        onSelect: (personneId) => navigate(`/personnes/${personneId}`)
      };
    }
    
    // Page ProjetDetail (pattern: /projets/:id)
    if (currentPath.match(/^\/projets\/\d+$/)) {
      const currentProjetId = currentPath.split('/')[2];
      return {
        type: 'projet',
        currentId: currentProjetId,
        items: state.projets?.data || [],
        placeholder: 'Choisir un projet...',
        getLabel: (item) => item.nom,
        getValue: (item) => item.id.toString(),
        onSelect: (projetId) => navigate(`/projets/${projetId}`)
      };
    }
    
    return null;
  };

  // Fonction pour déterminer si on affiche le filtre par catégorie
  const getCategoryFilter = () => {
    const currentPath = location.pathname;
    
    // Page Projets (exactement /projets)
    if (currentPath === '/projets') {
      return {
        type: 'category',
        currentValue: state.filters.categorie || '',
        onSelect: (categorie) => actions.setFilters({ categorie })
      };
    }
    
    return null;
  };

  const selectData = getSelectData();
  const categoryFilter = getCategoryFilter();

  const handleLogout = () => {
    actions.logout();
    setUserMenuOpen(false);
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar mobile */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-full max-w-xs flex-col bg-white shadow-xl">
          <div className="flex h-16 items-center justify-between px-4">
            <span className="text-xl font-bold text-gray-900">Akanea Reims</span>
            <button onClick={() => setSidebarOpen(false)} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
          <nav className="flex-1 px-2 py-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1 ${
                  isActive(item.href)
                    ? 'bg-primary-100 text-primary-900 border-r-2 border-primary-500'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Sidebar desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4 bg-primary-600">
            <span className="text-xl font-bold text-white">Time Tracker</span>
          </div>
          <nav className="flex-1 px-2 py-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1 ${
                  isActive(item.href)
                    ? 'bg-primary-100 text-primary-900 border-r-2 border-primary-500'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                className="lg:hidden text-gray-400 hover:text-gray-600"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu size={24} />
              </button>
              <div className="hidden lg:flex lg:items-center lg:space-x-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <DatePicker />
                <TeamSelector />
                {/* Select conditionnel pour PersonneDetail et ProjetDetail */}
                {selectData && (
                  <div className="relative">
                    <select
                      value={selectData.currentId}
                      onChange={(e) => selectData.onSelect(e.target.value)}
                      className="py-2 pl-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-sm"
                    >
                      <option value="">{selectData.placeholder}</option>
                      {selectData.items.map((item) => (
                        <option 
                          key={selectData.getValue(item)} 
                          value={selectData.getValue(item)}
                        >
                          {selectData.getLabel(item)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Filtre par catégorie pour la page Projets */}
                {categoryFilter && (
                  <div className="relative">
                    <select
                      value={categoryFilter.currentValue}
                      onChange={(e) => categoryFilter.onSelect(e.target.value)}
                      className="py-2 pl-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-sm"
                    >
                      <option value="">Toutes les catégories</option>
                      {AVAILABLE_CATEGORIES.map((category) => (
                        <option 
                          key={category.value} 
                          value={category.value}
                        >
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Bell className="text-gray-400 hover:text-gray-600 cursor-pointer" size={20} />
                {state.notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {state.notifications.length}
                  </span>
                )}
              </div>
              
              {/* Menu utilisateur */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
                >
                  <div className="h-8 w-8 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {state.auth.user?.prenom?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <span className="hidden md:block text-sm font-medium">
                    {state.auth.user ? `${state.auth.user.prenom} ${state.auth.user.nom}` : 'Utilisateur'}
                  </span>
                  <ChevronDown size={16} className={`transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown menu */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                      <div className="font-medium">{state.auth.user?.prenom} {state.auth.user?.nom}</div>
                      <div className="text-gray-500">{state.auth.user?.email}</div>
                    </div>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate('/profil');
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <User size={16} className="mr-2" />
                      Profil
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <LogOut size={16} className="mr-2" />
                      Se déconnecter
                    </button>
                  </div>
                )}
                
                {/* Overlay pour fermer le menu en cliquant dehors */}
                {userMenuOpen && (
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setUserMenuOpen(false)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Notifications */}
      {state.notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {state.notifications.map((notification) => (
            <div
              key={notification.id}
              className={`p-4 rounded-lg shadow-lg max-w-sm ${
                notification.type === 'success' 
                  ? 'bg-green-100 border border-green-400 text-green-700' 
                  : 'bg-red-100 border border-red-400 text-red-700'
              }`}
            >
              <div className="flex justify-between items-start">
                <p className="text-sm">{notification.message}</p>
                <button
                  onClick={() => actions.removeNotification(notification.id)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Layout; 